import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { add, parseDate } from 'helpers/date_utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTable } from '@fortawesome/pro-solid-svg-icons';

import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import LeaseTerm from './lease_term';
import Aggregates from './aggregates';
import DisableOperatingExpensesModal from './disable_operating_expenses_modal';
import RentEscalationModal from './rent_escalation_modal';
import {
  commissionModes, FLAT_RATE,
  PERCENT_OF_RENT, RATE_PER_SPACE_SIZE_UNIT
} from './commission_modes';
import {
  updateLeaseTerm,
  updateLeaseTermByTransactionSide,
  calculateLeaseTermsFromEscalation
} from './helpers/lease_term';
import { commissionUnitsLabel, leaseRateUnitsLabel } from './helpers/select_options';
import AddControl from 'components/add_control';
import Table from 'bundles/backoffice/vouchers/editor/table';
import Toggle from 'components/toggle';
import AutoCalcToggleModal from '../auto_calc_toggle_modal';
import {
  BUY_SIDE,
  getTransactionSideLabel,
  newTransactionSideDetails,
  SELL_SIDE,
} from 'bundles/backoffice/vouchers/helpers/transaction_side_details';

import './styles.scss';

export class LeaseStructureTerms extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    commissionPercent: PropTypes.number,
    commissionRate: PropTypes.number,
    controlWrapper: PropTypes.any,
    duration: PropTypes.number,
    durationUnits: PropTypes.string,
    enableOperatingExpenses: PropTypes.bool,
    forceControlRendering: PropTypes.bool,
    initialLeaseRate: PropTypes.number,
    leaseRateUnits: PropTypes.string,
    leaseStructure: PropTypes.shape({
      commissionMode: PropTypes.oneOf(commissionModes).isRequired,
      includeOperatingExpenses: PropTypes.bool,
      isRentCalculated: PropTypes.bool.isRequired,
      leaseRateUnits: PropTypes.string.isRequired,
      leaseTerms: PropTypes.arrayOf(PropTypes.shape({
        index: PropTypes.number.isRequired
      })).isRequired,
      spaceSize: PropTypes.number,
      spaceSizeUnits: PropTypes.string.isRequired,
    }).isRequired,
    readOnly: PropTypes.bool,
    readOnlyActions: PropTypes.node,
    showHeader: PropTypes.bool,
    showTransactionSides: PropTypes.bool,
    transactionSideOptions: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    onLeaseStructureChange: PropTypes.func.isRequired,
    onShowRentEscalationModal: PropTypes.func,
  }

  static defaultProps = {
    showHeader: true,
  }

  state = {}

  get commissionRateSuffix() {
    return this.props.leaseStructure.commissionMode === PERCENT_OF_RENT ? '%' : 'Rate';
  }

  get undestroyedLeaseTerms() {
    return this.props.leaseStructure.leaseTerms.filter(leaseTerm => !leaseTerm._destroy);
  }

  get hideNotesColumn() {
    return this.undestroyedLeaseTerms.map(term => term.notes).every(notes => !notes) && this.props.readOnly;
  }

  areCommissionColumnsShown() {
    const { commissionMode } = this.props.leaseStructure;
    return ![RATE_PER_SPACE_SIZE_UNIT, FLAT_RATE].includes(commissionMode);
  }

  copyPreviousLeaseTerm(leaseTerm) {
    const nextLeaseTerm = this.undestroyedLeaseTerms.reverse().find(term => term.index < leaseTerm.index);
    return nextLeaseTerm ? { ...nextLeaseTerm } : null;
  }

  copyNextLeaseTerm(leaseTerm) {
    const previousLeaseTerm = this.undestroyedLeaseTerms.find(term => term.index > leaseTerm.index);
    return previousLeaseTerm ? { ...previousLeaseTerm } : null;
  }

  NEW_TRANSACTION_SIDE_DETAILS = [
    newTransactionSideDetails(SELL_SIDE, true),
    newTransactionSideDetails(BUY_SIDE, true),
  ];

  insertLeaseTerm = (index, startDate, targetLeaseTerm) => {
    if (index > this.undestroyedLeaseTerms.slice(-1)[0].index) {
      this.addLeaseTerm();
      return;
    }

    const leaseTerm = {
      commissionAmount: targetLeaseTerm.commissionAmount,
      commissionPercent: targetLeaseTerm.commissionPercent,
      commissionRate: targetLeaseTerm.commissionRate,
      commissionUnits: targetLeaseTerm.commissionUnits,
      endDate: startDate,
      leaseAmount: targetLeaseTerm.leaseAmount,
      leaseRate: targetLeaseTerm.leaseRate,
      months: 0,
      operatingExpensesRate: targetLeaseTerm.operatingExpensesRate,
      startDate,
    };

    if (targetLeaseTerm.transactionSideDetails) {
      leaseTerm.transactionSideDetails = targetLeaseTerm.transactionSideDetails.map(side => ({ ...side, id: null }));
    }

    const newLeaseTerms = [...this.props.leaseStructure.leaseTerms];
    newLeaseTerms.splice(index, 0, leaseTerm);
    newLeaseTerms.forEach((item, index) => item.index = index);

    this.props.onChange(newLeaseTerms);
  }

  addLeaseTerm = () => {
    let leaseTerm = {
      commissionAmount: 0,
      commissionPercent: 0,
      commissionRate: 0,
      commissionUnits: 'commission_units_percent',
      operatingExpensesRate: 0,
      transactionSideDetails: [],
      index: this.props.leaseStructure.leaseTerms.length
    };

    if (this.undestroyedLeaseTerms.length > 0) {
      const lastLeaseTerm = this.undestroyedLeaseTerms[this.undestroyedLeaseTerms.length - 1];

      leaseTerm.leaseRate = lastLeaseTerm.leaseRate;
      leaseTerm.commissionPercent = lastLeaseTerm.commissionPercent;
      leaseTerm.commissionRate = lastLeaseTerm.commissionRate;
      leaseTerm.operatingExpensesRate = lastLeaseTerm.operatingExpensesRate;

      if (lastLeaseTerm.transactionSideDetails) {
        leaseTerm.transactionSideDetails.push(
          ...lastLeaseTerm.transactionSideDetails.map(side => ({ ...side, id: null }))
        );
      }

      let updateKey;
      if (lastLeaseTerm.endDate) {
        leaseTerm.startDate = add(parseDate(lastLeaseTerm.endDate), 1, 'day').toISOString();
        updateKey = 'startDate';
      }

      if (lastLeaseTerm.months) {
        leaseTerm.months = lastLeaseTerm.months;
        updateKey = updateKey || 'months';
      }

      updateKey = updateKey || 'startDate';

      // Triggering a change through either startDate or months should affect everything
      leaseTerm = updateLeaseTerm(this.props.leaseStructure, leaseTerm, updateKey);
    } else if (this.props.showTransactionSides) {
      leaseTerm.transactionSideDetails.push(...this.NEW_TRANSACTION_SIDE_DETAILS);
    }

    this.props.onChange([...this.props.leaseStructure.leaseTerms, leaseTerm]);
  }

  deleteLeaseTerm = (leaseTerm) => {
    leaseTerm = { ...leaseTerm, _destroy: true };

    const leaseTerms = [...this.props.leaseStructure.leaseTerms];
    leaseTerms[leaseTerm.index] = leaseTerm;

    const previousLeaseTerm = this.copyPreviousLeaseTerm(leaseTerm);
    const nextLeaseTerm = this.copyNextLeaseTerm(leaseTerm);
    if (nextLeaseTerm) {
      if (previousLeaseTerm && previousLeaseTerm.endDate) {
        nextLeaseTerm.startDate = add(parseDate(previousLeaseTerm.endDate), 1, 'day').toISOString();
      } else {
        nextLeaseTerm.startDate = null;
      }

      leaseTerms[nextLeaseTerm.index] =
        updateLeaseTerm(this.props.leaseStructure, nextLeaseTerm, 'startDate');
    }

    this.props.onChange(leaseTerms);
  }

  handleChange = (leaseTerm, key, value, transactionSide = null) => {
    if (transactionSide) {
      leaseTerm = updateLeaseTermByTransactionSide(this.props.leaseStructure, leaseTerm, key, value, transactionSide);
    } else {
      leaseTerm = updateLeaseTerm(this.props.leaseStructure, { ...leaseTerm, [key]: value }, key);
    }

    const leaseTerms = [...this.props.leaseStructure.leaseTerms];
    leaseTerms[leaseTerm.index] = leaseTerm;

    const nextLeaseTerm = this.copyNextLeaseTerm(leaseTerm);
    if (nextLeaseTerm) {
      nextLeaseTerm.startDate = leaseTerm.endDate ? add(parseDate(leaseTerm.endDate), 1, 'day').toISOString() : null;
      leaseTerms[nextLeaseTerm.index] = updateLeaseTerm(this.props.leaseStructure, nextLeaseTerm, 'startDate');
    }

    this.props.onChange(leaseTerms);
  }

  showModal = () => {
    if (this.props.onShowRentEscalationModal) {
      this.props.onShowRentEscalationModal();
    } else {
      this.setState({ showModal: true });
    }
  }

  hideModal = () => {
    this.setState({ showModal: false });
  }

  handleAddEscalationTerms = (escalation) => {
    const { leaseStructure } = this.props;
    leaseStructure.leaseRateUnits = escalation.leaseRateUnits;

    this.props.onLeaseStructureChange({
      leaseRateUnits: escalation.leaseRateUnits,
      includeOperatingExpenses: escalation.includeOperatingExpenses,
    });

    const newLeaseTerms = calculateLeaseTermsFromEscalation(escalation, leaseStructure);
    newLeaseTerms.forEach((leaseTerm, i) => {
      newLeaseTerms[i] = leaseTerm.transactionSideDetails?.length
        ? updateLeaseTermByTransactionSide(this.props.leaseStructure, leaseTerm)
        : updateLeaseTerm(this.props.leaseStructure, leaseTerm, 'startDate');
    });

    this.props.onChange([...this.props.leaseStructure.leaseTerms, ...newLeaseTerms]);
  }

  render() {
    let content;
    if (this.undestroyedLeaseTerms.length > 0) {
      content = <>
        <Table
          className="lease-terms"
          empty={this.undestroyedLeaseTerms.length === 0}
          readOnly={!this.props.forceControlRendering && this.props.readOnly}
          sticky
        >
          {this.renderTableHead()}

          <tbody>
            {this.undestroyedLeaseTerms.map(this.renderLeaseTerm)}
          </tbody>

          <tfoot>
            <Aggregates
              hideNotes={this.hideNotesColumn}
              includeOperatingExpenses={this.props.leaseStructure.includeOperatingExpenses}
              leaseStructure={this.props.leaseStructure}
              showCommissionColumns={this.areCommissionColumnsShown()}
              showTransactionSides={this.props.showTransactionSides}
            />
          </tfoot>
        </Table>
      </>;
    } else {
      content = (
        <div className="text-center">
          <Empty className="bg-white">
            <Empty.Media>
              <FontAwesomeIcon icon={faTable} />
            </Empty.Media>
            <Empty.Content>No lease terms have been added yet.</Empty.Content>
            <Empty.Actions>
              {this.renderAddControl('Add a Rent Escalation', { name: 'show-modal-control', onClick: this.showModal })}
            </Empty.Actions>
          </Empty>
        </div>
      );
    }

    return (
      <div className={classNames('full-width', this.props.className)}>
        {this.renderSectionHeader()}

        {content}

        <RentEscalationModal
          commissionMode={this.props.leaseStructure.commissionMode}
          commissionPercent={this.props.commissionPercent}
          commissionRate={this.props.commissionRate}
          duration={this.props.duration}
          durationUnits={this.props.durationUnits}
          enableOperatingExpenses={this.props.enableOperatingExpenses}
          initialLeaseRate={this.props.initialLeaseRate}
          leaseRateUnits={this.props.leaseRateUnits}
          show={this.state.showModal}
          spaceSizeUnits={this.props.leaseStructure.spaceSizeUnits}
          transactionSideOptions={this.props.transactionSideOptions}
          useTransactionSideValues={this.props.showTransactionSides}
          onClose={this.hideModal}
          onSubmit={this.handleAddEscalationTerms}
        />
      </div>
    );
  }

  hideAutoCalcModal = () => {
    this.setState({ showAutoCalcModal: false });
  }

  handleToggleChange = (checked) => {
    this.props.onLeaseStructureChange({ isRentCalculated: checked });
    this.hideAutoCalcModal();
  }

  handleIncludeOperatingExpensesChange = () => {
    if (this.props.leaseStructure.includeOperatingExpenses) {
      if (this.undestroyedLeaseTerms.every(leaseTerm => leaseTerm.operatingExpensesRate === 0)) {
        this.props.onLeaseStructureChange({ includeOperatingExpenses: false });
      } else {
        this.setState({ showDisableOperatingExpensesModal: true });
      }
    } else {
      this.props.onLeaseStructureChange({ includeOperatingExpenses: true });
    }
  }

  disableOperatingExpenses = (keyToKeep) => {
    this.setState({ showDisableOperatingExpensesModal: false });
    this.props.onLeaseStructureChange({ includeOperatingExpenses: false });
    this.props.onChange(this.props.leaseStructure.leaseTerms.map((leaseTerm) => {
      return updateLeaseTerm(this.props.leaseStructure, { ...leaseTerm, operatingExpensesRate: 0 }, keyToKeep);
    }));
  };

  renderSectionHeader() {
    const { forceControlRendering, readOnly, showHeader, leaseStructure } = this.props;

    return (
      <div>
        <div>
          {showHeader && <h5>Lease Terms</h5>}
          {(forceControlRendering || !readOnly) && (
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center mb-1">
                <span className="me-2">Auto-calculate Rents</span>
                <Toggle
                  checked={leaseStructure.isRentCalculated}
                  onChange={() => this.setState({ showAutoCalcModal: true })}
                />

                {this.props.enableOperatingExpenses &&
                  <React.Fragment>
                    <span className="ms-4 me-2">Operating expenses</span>
                    <Toggle
                      checked={leaseStructure.includeOperatingExpenses}
                      onChange={this.handleIncludeOperatingExpensesChange}
                    />

                    <DisableOperatingExpensesModal
                      show={this.state.showDisableOperatingExpensesModal}
                      onCancel={() => this.setState({ showDisableOperatingExpensesModal: false })}
                      onKeepLeaseTermValue={this.disableOperatingExpenses}
                    />
                  </React.Fragment>
                }
              </div>
              {this.renderAddControl('Add Term', { name: 'add-lease-term', onClick: this.addLeaseTerm })}
            </div>
          )}
          <AutoCalcToggleModal
            handleToggleChange={this.handleToggleChange}
            isToggleChecked={leaseStructure.isRentCalculated}
            show={this.state.showAutoCalcModal}
            onHide={this.hideAutoCalcModal}
          />
        </div>
      </div>
    );
  }

  renderTableHead() {
    const { leaseStructure } = this.props;
    const classDollarColumn =  classNames('dollar-column', { 'full-width': this.hideNotesColumn });

    return (
      <thead>
        <tr>
          <Table.th className="date-column">Start Date</Table.th>
          <Table.th className="date-column">End Date</Table.th>
          <Table.th className="months-column">Months</Table.th>
          {leaseStructure.includeOperatingExpenses &&
          <Table.th className={classDollarColumn} numeric withTooltip>
            OpEx Rate {leaseRateUnitsLabel(leaseStructure)}
          </Table.th>
          }
          <Table.th className={classDollarColumn} numeric withTooltip>
            {leaseStructure.includeOperatingExpenses && 'Base '}
            Lease Rate {leaseRateUnitsLabel(leaseStructure)}
          </Table.th>
          <Table.th className={classDollarColumn} numeric>Total Rent</Table.th>
          {
            this.areCommissionColumnsShown() &&
              this.renderCommissionColumns(leaseStructure, classDollarColumn)
          }
          {!this.hideNotesColumn && <Table.th>Notes</Table.th>}
          <Table.th sticky />
        </tr>
      </thead>
    );
  }

  renderCommissionColumns(leaseStructure, classDollarColumn) {
    const transactionSides = this.props.showTransactionSides
      ? [
        getTransactionSideLabel(SELL_SIDE, this.props.transactionSideOptions),
        getTransactionSideLabel(BUY_SIDE, this.props.transactionSideOptions),
      ]
      : [''];
    return (
      <React.Fragment>
        {transactionSides.map(side => (
          <React.Fragment key={side || 'commission-columns'}>
            <Table.th className={classDollarColumn} numeric withTooltip>
              Commission {this.commissionRateSuffix} {commissionUnitsLabel(leaseStructure)}{side && ` (${side})`}
            </Table.th>
            <Table.th className={classDollarColumn} numeric withTooltip>
              Commission ${side && ` (${side})`}
            </Table.th>
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  }

  renderLeaseTerm = (leaseTerm, index) => {
    return (
      <LeaseTerm
        commissionMode={this.props.leaseStructure.commissionMode}
        controlWrapper={this.props.controlWrapper}
        forceControlRendering={this.props.forceControlRendering}
        hideNotes={this.hideNotesColumn}
        includeOperatingExpenses={this.props.leaseStructure.includeOperatingExpenses}
        isFirst={index === 0}
        key={leaseTerm.index}
        leaseTerm={leaseTerm}
        readOnly={this.props.readOnly}
        readOnlyActions={this.props.readOnlyActions}
        showCommissionColumns={this.areCommissionColumnsShown()}
        showTransactionSides={this.props.showTransactionSides}
        onChange={this.handleChange}
        onDelete={this.deleteLeaseTerm}
        onInsert={this.insertLeaseTerm}
      />
    );
  }

  renderAddControl(label, props) {
    if (this.props.controlWrapper) {
      const ControlWrapper = this.props.controlWrapper;
      return (
        <ControlWrapper {...props}>
          <AddControl aria-label={props.name}>{label}</AddControl>
        </ControlWrapper>
      );
    } else {
      return <AddControl aria-label={props.name} {...props}>{label}</AddControl>;
    }
  }
}

export default LeaseStructureTerms;
